/* eslint-disable @typescript-eslint/no-misused-promises */
import { FormEvent, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { CardWrapper } from "@/components/card-wrapper";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MailCheck } from "lucide-react";
import { RequestSource } from "@/lib/errorHandling";
import { toast } from "sonner";
import { useHandleError } from "@/hooks/useHandleError";
import { usePocketBase } from "@/context/pocketbase-context";
import { useTranslation } from "react-i18next";
import { validateEmail } from "@/lib/validators";

export function PasswordRecoveryPage() {
  const { t } = useTranslation(["pages", "common"]);
  const { handleError } = useHandleError();
  const { recoverPassword } = usePocketBase();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    setIsFormValid(email !== "" && emailError === "");
  }, [email, emailError]);

  const handlePasswordRecovery = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateEmail(email, setEmailError);
    if (!isFormValid) {
      return;
    }
    try {
      const res = await recoverPassword(email);
      if (res) {
        toast.success(t("pages:passwordRecovery.emailSentToast"));
        setEmailSent(true);
      }
    } catch (error) {
      toast.error(handleError(RequestSource.PASSWORD_RECOVERY, error));
    }
  };

  if (emailSent) {
    return (
      <div className="pt-12 flex justify-center items-center w-screen h-screen p-4">
        <CardWrapper className="w-[380px] md:w-[450px]">
          <div className="space-y-6 p-4 px-8 text-center">
            <div className="space-y-4">
              <h2 className="mt-6 text-3xl font-bold text-foreground">
                {t("pages:passwordRecovery.emailSentTitle")}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t("pages:passwordRecovery.emailSentMessage", { email })}
              </p>
            </div>
            <div className="my-10">
              <div className="flex justify-center animate-in fade-in zoom-in duration-500">
                <div className="rounded-full bg-primary p-6">
                  <MailCheck className="w-16 h-16 text-white" />
                </div>
              </div>
            </div>
            <a
              href="/login"
              className="block text-sm underline text-foreground"
            >
              {t("common:buttons.backToLogin")}
            </a>
          </div>
        </CardWrapper>
      </div>
    );
  }

  return (
    <div className="pt-12 flex justify-center items-center w-screen h-screen p-4">
      <CardWrapper className="w-[380px] md:w-[450px]">
        <div className="space-y-6 p-4 px-8">
          <div className="space-y-2 text-center">
            <h2 className="mt-6 text-3xl font-bold text-foreground">
              {t("pages:passwordRecovery.passwordRecoveryTitle")}
            </h2>
            <p className="hidden md:inline text-gray-500 dark:text-gray-400">
              {t("pages:passwordRecovery.passwordRecoverySubtitle")}
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handlePasswordRecovery}>
            <div className="space-y-4 rounded-md shadow-sm">
              <div>
                <Label htmlFor="email-address">{t("common:email")}</Label>
                <Input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    validateEmail(e.target.value, setEmailError);
                  }}
                  className={emailError ? "border-red-500" : ""}
                />
                {emailError && (
                  <p className="text-sm text-red-500">{emailError}</p>
                )}
              </div>
            </div>
            <div>
              <Button type="submit" className="w-full" disabled={!isFormValid}>
                {t("pages:passwordRecovery.sendButton")}
              </Button>
            </div>
          </form>
        </div>
      </CardWrapper>
    </div>
  );
}
